import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Phone component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconPhone extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M4.19332 2.86725C4.61023 2.45966 5.17011 2.23145 5.75315 2.23145C6.33619 2.23145 6.89607 2.45966 7.31297 2.86725L7.31852 2.87267L10.0393 5.59347C10.4467 6.00807 10.675 6.56614 10.675 7.14744C10.675 7.72875 10.4467 8.2868 10.0392 8.7014L9.54916 8.21976L10.0315 8.70918C9.95325 8.78626 9.89114 8.87812 9.84875 8.97941C9.80636 9.08071 9.78452 9.18942 9.78452 9.29923C9.78452 9.40904 9.80636 9.51776 9.84875 9.61905C9.89114 9.72035 9.95325 9.81221 10.0315 9.88929L10.035 9.89282L14.3212 14.179C14.4811 14.3341 14.6951 14.4209 14.9179 14.4209C15.1419 14.4209 15.3569 14.3332 15.517 14.1767L15.9974 14.668L15.5158 14.1779C15.9304 13.7705 16.4884 13.5421 17.0697 13.5421C17.651 13.5421 18.2091 13.7705 18.6237 14.1779L18.6253 14.1796L21.3643 16.8898C21.7719 17.3067 22 17.8667 22 18.4497C22 19.0327 21.7718 19.5926 21.3642 20.0095L21.3533 20.0205L20.7141 20.6454C19.8678 21.501 18.7531 22.0398 17.5568 22.1714C16.3584 22.3032 15.1511 22.0184 14.1379 21.3648L14.5104 20.7874L14.1261 21.3569C9.68409 18.3596 5.85572 14.5411 2.8469 10.1068L2.83809 10.0935C2.18448 9.08038 1.89965 7.87309 2.03147 6.67465C2.16307 5.47832 2.70186 4.36363 3.55743 3.51734L4.18238 2.87819L4.19332 2.86725ZM14.8887 20.2137C15.6353 20.6935 16.5242 20.9024 17.4065 20.8054C18.2912 20.7081 19.1153 20.3091 19.7404 19.6756L19.749 19.6669L20.3861 19.0441C20.5398 18.8845 20.6257 18.6714 20.6257 18.4497C20.6257 18.2274 20.5393 18.0138 20.3849 17.854L17.6604 15.1581L17.6596 15.1573C17.5021 15.0029 17.2903 14.9164 17.0697 14.9164C16.8487 14.9164 16.6366 15.0032 16.479 15.1581L15.9974 14.668L16.4777 15.1593C16.0608 15.5669 15.501 15.7951 14.9179 15.7951C14.3349 15.7951 13.775 15.5669 13.3581 15.1593L13.3525 15.1539L9.06485 10.8661L9.06686 10.8681L9.54913 10.3787L9.06329 10.8646L9.06485 10.8661C8.85783 10.6616 8.69339 10.4181 8.58104 10.1496C8.46832 9.88027 8.41027 9.59121 8.41027 9.29923C8.41027 9.00726 8.46832 8.7182 8.58104 8.44886C8.69289 8.18159 8.85637 7.93903 9.06208 7.73507C9.21509 7.57779 9.30076 7.36698 9.30076 7.14744C9.30076 6.92732 9.21463 6.71596 9.06085 6.55855L6.3499 3.8476C6.19002 3.6925 5.97599 3.6057 5.75315 3.6057C5.53141 3.6057 5.3184 3.69164 5.15878 3.8453L4.52728 4.49115C3.89381 5.11623 3.4948 5.94029 3.39749 6.82491C3.30046 7.70698 3.50922 8.59554 3.98872 9.34204C6.89716 13.6265 10.5967 17.3164 14.8887 20.2137Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-phone') === undefined) {
  customElements.define('cat-icon-phone', BlocksIconPhone);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-phone': BlocksIconPhone;
  }
}
